import { Identificable } from 'shared/types/commonView';
import {
  DirectoriesBatchCreateModel,
  DirectoriesBatchCreatedModel,
} from 'shared/domain/directory/directoryModel';
import {
  normalizeBatchRequest,
  flattenNestedDirectories,
  mapResponseToCreatedItems,
  enforceRequestUniqueness,
  FlattenedBatchNode,
  NestedDirectoryBatchInDto,
} from 'shared/domain/directory/mapping';
import { DirectoryBatchInDto } from 'shared/dtos/in/directory';
import { Fetcher } from '../httpQueue/fetcher';
import {
  fastAddBatch as addDirectoriesBatch,
  getByQuery as getDirectoriesByParent,
} from 'serviceWorker/db/directories/directories';
import { DirectoryEntity } from 'serviceWorker/repository/directory/entity';
import { broadcastDirectoriesBatchCreated } from 'serviceWorker/broadcasts/directories';
import { get as getCurrentProject } from 'serviceWorker/db/selectedProject';
import { scopeOfProject } from 'serviceWorker/helpers/dataScope';

const ROOT_PARENT_KEY = '__ROOT__';

class DirectoriesBatchSaver {
  constructor(
    private fetcher: typeof Fetcher,
    private addBatch: (data: DirectoryBatchInDto[]) => Promise<any>,
    private getCurrentProject: () => Promise<Identificable | undefined>,
    private getDirectoriesByParent: (
      query: Partial<DirectoryEntity>
    ) => Promise<DirectoryEntity[]>,
    private broadcastCreated: (
      payload: DirectoriesBatchCreatedModel,
      uniqueId: string
    ) => void
  ) {}

  async execute(
    directoriesModel: DirectoriesBatchCreateModel,
    uniqueId: string
  ): Promise<void> {
    const project = await this.getCurrentProject();
    if (!project?._id) {
      throw new Error('Current project not available.');
    }

    const { requestBody, flatNodes } = normalizeBatchRequest(directoriesModel);
    await this.validateDuplicates(flatNodes);

    const url = `/project/${project._id}/dirs/batch`;
    const response = (await this.fetcher.POST(url, requestBody)) as {
      directories: NestedDirectoryBatchInDto[];
    };

    if (!response?.directories?.length) {
      this.broadcastCreated({ directories: [] }, uniqueId);
      return;
    }

    const flatDirectories = flattenNestedDirectories(response.directories);
    // Stored only in its project; answered in any case (the server created them).
    await scopeOfProject(project._id).write(() => this.addBatch(flatDirectories));

    const mapping = mapResponseToCreatedItems(flatDirectories, flatNodes);
    this.broadcastCreated({ directories: mapping }, uniqueId);
  }

  private async validateDuplicates(
    nodes: FlattenedBatchNode[]
  ): Promise<void> {
    enforceRequestUniqueness(nodes);

    const parentsToVerify = Array.from(
      new Set(
        nodes
          .filter((node) => node.parentId || node.isRootDirectory)
          .map((node) => (node.parentId ? node.parentId : ROOT_PARENT_KEY))
      )
    );

    const existingByParent = new Map<string, Set<string>>();
    await Promise.all(
      parentsToVerify.map(async (parentKey) => {
        const parentId = parentKey === ROOT_PARENT_KEY ? '' : parentKey;
        const existing = await this.getDirectoriesByParent({ parentId });
        existingByParent.set(
          parentKey,
          new Set(existing.map((d) => d.name.toLocaleLowerCase()))
        );
      })
    );

    nodes.forEach((node) => {
      const parentKey =
        node.parentId ?? (node.isRootDirectory ? ROOT_PARENT_KEY : '');
      if (!existingByParent.has(parentKey)) {
        return;
      }
      const isDuplicate = existingByParent
        .get(parentKey)
        ?.has(node.name.toLocaleLowerCase());
      if (isDuplicate) {
        throw new Error(
          `Folder "${node.name}" already exists in target location.`
        );
      }
    });
  }
}

export const directoriesBatchSaver = new DirectoriesBatchSaver(
  Fetcher,
  addDirectoriesBatch,
  getCurrentProject,
  getDirectoriesByParent,
  broadcastDirectoriesBatchCreated
);
